import React, { useEffect, useMemo, useRef } from "react";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const CustomCursor = ({ enabled = true }) => {
  const dotRef = useRef(null);
  const ringRef = useRef(null);

  const state = useMemo(
    () => ({
      x: 0,
      y: 0,
      rx: 0,
      ry: 0,
      hovering: false,
      raf: 0,
    }),
    []
  );

  useEffect(() => {
    if (!enabled) return undefined;
    const dot = dotRef.current;
    const ring = ringRef.current;
    if (!dot || !ring) return undefined;

    const onMove = (e) => {
      state.x = e.clientX;
      state.y = e.clientY;
      if (!state.raf) tick();
    };

    const onOver = (e) => {
      const t = e.target;
      const interactive =
        t?.closest?.(
          'a,button,[role="button"],input,select,textarea,summary,label,[data-cursor="hover"]'
        ) != null;
      state.hovering = interactive;
      ring.dataset.hover = interactive ? "1" : "0";
    };

    const tick = () => {
      state.raf = requestAnimationFrame(() => {
        state.raf = 0;
        // Dot: snappy but smoothed to avoid jitter on high-DPI mice
        const dx = state.x - state.rx;
        const dy = state.y - state.ry;
        state.rx += dx * 0.22;
        state.ry += dy * 0.22;
        const x = clamp(state.rx, -99999, 99999);
        const y = clamp(state.ry, -99999, 99999);
        dot.style.transform = `translate3d(${x}px, ${y}px, 0)`;
        ring.style.transform = `translate3d(${x}px, ${y}px, 0)`;
        if (Math.abs(state.x - state.rx) > 0.25 || Math.abs(state.y - state.ry) > 0.25) tick();
      });
    };

    document.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseover", onOver, { passive: true });

    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver);
      if (state.raf) cancelAnimationFrame(state.raf);
      state.raf = 0;
    };
  }, [enabled, state]);

  if (!enabled) return null;

  return (
    <>
      <div
        ref={dotRef}
        className="pointer-events-none fixed left-0 top-0 z-[9999] h-2 w-2 -translate-x-1 -translate-y-1 rounded-full bg-gradient-to-r from-pink-400 via-rose-400 to-yellow-300 shadow-[0_0_22px_rgba(236,72,153,0.35)]"
        aria-hidden
      />
      <div
        ref={ringRef}
        data-hover="0"
        className="cursor-ring pointer-events-none fixed left-0 top-0 z-[9998] h-10 w-10 -translate-x-5 -translate-y-5 rounded-full border border-pink-400/50 bg-white/10 backdrop-blur-sm"
        aria-hidden
      />
    </>
  );
};

export default CustomCursor;

